import { Badge, Button, Icon, RowCard } from "@elaniintc/goes-design-system";
import { NavLink, useParams } from "react-router";
import dayjs from "dayjs";
import type { FlowExecutionType } from "@/types/entities/flows";
import { useMemo } from "react";
import { UsersListTooltip } from "@/components/common/userlist-tooltip";
import { mapInteractionsToUsersList } from "@/components/common/userlist-tooltip.utils";

interface PaymentCardProps {
  paymentRequestId: string;
  nodeExecution: FlowExecutionType["node_executions"][number];
  procedureId: string;
  index: number;
}

/**
 * Payment card component for displaying payment node in the procedures list
 */
export const PaymentCard = ({
  paymentRequestId,
  nodeExecution,
  procedureId,
  index,
}: PaymentCardProps) => {
  const startedAt = dayjs(nodeExecution.started_at).format(
    "DD/MM/YYYY HH:mm A",
  );
  const paymentRequest = nodeExecution.payment_request;

  const getBadgeByStatus = (status: string) => {
    switch (status) {
      case "PAID":
        return (
          <Badge size="xs" variant="success" shape="square">
            Completado
            <Icon
              type="fas"
              name="check"
              className="text-success-quiet text-xs"
            />
          </Badge>
        );
      case "PENDING":
        return (
          <Badge size="xs" variant="warning" shape="square">
            Pendiente
            <Icon
              type="fas"
              name="clock"
              className="text-warning-quiet text-xs"
            />
          </Badge>
        );
      case "REJECTED":
        return (
          <Badge size="xs" variant="danger" shape="square">
            Rechazado
            <Icon
              type="fas"
              name="triangle-exclamation"
              className="text-danger-quiet text-xs"
            />
          </Badge>
        );
      case "EXEMPT":
        return (
          <Badge size="xs" variant="neutral" shape="square">
            Eximido
            <Icon
              type="fas"
              name="circle-minus"
              className="text-body-quiet text-xs"
            />
          </Badge>
        );
      default:
        return null;
    }
  };

  const getCardColor = (status: string) => {
    switch (status) {
      case "PAID":
        return "success";
      case "PENDING":
        return "warning";
      case "REJECTED":
        return "danger";
      case "EXEMPT":
        return "neutral";
      default:
        return "neutral";
    }
  };

  // Obtener el título del pago desde los inputs o metadata
  const titleInput = nodeExecution.inputs?.find(
    (input) => input.name === "title",
  );

  // Parsear metadata si existe
  let metadataTitle: string | undefined;
  if (typeof paymentRequest?.metadata === "string") {
    try {
      const parsedMetadata = JSON.parse(paymentRequest.metadata);
      metadataTitle = parsedMetadata?.title;
    } catch (e) {
      console.error("Error parsing metadata:", e);
    }
  } else if (
    paymentRequest?.metadata &&
    typeof paymentRequest.metadata === "object"
  ) {
    const metadata = paymentRequest.metadata as Record<string, unknown>;
    metadataTitle =
      typeof metadata.title === "string" ? metadata.title : undefined;
  }

  const paymentTitle =
    metadataTitle ||
    (typeof titleInput?.value === "string"
      ? titleInput.value
      : nodeExecution.node?.display_name) ||
    "Nodo de pago";

  const params = useParams<{
    procedureId: string;
    id?: string;
    registryId?: string;
  }>();

  const redirectUrl = useMemo(() => {
    if (params.id && params.registryId) {
      return `/expedientes/entidad/${params.id}/detalle-registro/${params.registryId}`;
    }
    return `/tramite`;
  }, [params]);

  return (
    <NavLink to={`${redirectUrl}/${procedureId}/pago/${paymentRequestId}`}>
      <RowCard
        key={paymentRequestId ?? index}
        title={
          <div className="flex items-center gap-1">
            <p className="font-normal text-base text-body-loud truncate leading-tight">
              {paymentTitle}
            </p>
          </div>
        }
        color={getCardColor(paymentRequest?.status || "PENDING")}
        icon={{
          name: "credit-card",
          type: "fas",
          size: "lg",
        }}
        subtitle={`Iniciado ${startedAt}`}
      >
        <RowCard.Right>
          {getBadgeByStatus(paymentRequest?.status || "PENDING")}

          {nodeExecution.interactions &&
            nodeExecution.interactions.length > 0 && (
              <UsersListTooltip
                items={mapInteractionsToUsersList(nodeExecution.interactions)}
              />
            )}

          <Button variant="ghost" size="xs" className="size-8">
            <Icon type="far" name="arrow-up-right" />
          </Button>
        </RowCard.Right>
      </RowCard>
    </NavLink>
  );
};
